import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap";
import classNames from "classnames";
import {
    MdStorefront,
    MdPayment,
    MdLanguage,
    MdInfoOutline,
    MdReceiptLong,
} from "react-icons/md";
import { MenuBurgerIcon } from "@aureskonnect/react-ui";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import {
    setIsModalOpened,
    setShowPropagationConfirm,
    savePosConfig,
    persistCurrentLevelOnly,
    clearErrorMessage,
} from "./posConfigSlice";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { SalesMode } from "./SalesMode";
import { PaymentMode } from "./PaymentMode";
import { Language } from "./Language";
import { InformationMode } from "./InformationMode";
import { PaperlessTicket } from "./PaperlessTicket";
import { POSSalesModeItem, POSInformationModeItem } from "./constants";
import "@components/ModalContentWrapper/index.css";
import "./index.css";

interface TabConfig {
    titleKey: string;
    component: React.ComponentType;
    icon: React.ComponentType<{ size?: number }>;
}

const TAB_CONFIG: TabConfig[] = [
    { titleKey: "Sale mode", component: SalesMode, icon: MdStorefront },
    { titleKey: "Payment mode", component: PaymentMode, icon: MdPayment },
    { titleKey: "Language", component: Language, icon: MdLanguage },
    {
        titleKey: "Information mode",
        component: InformationMode,
        icon: MdInfoOutline,
    },
    {
        titleKey: "Paperless ticket",
        component: PaperlessTicket,
        icon: MdReceiptLong,
    },
];

const INFORMATION_MODE_TAB_INDEX = 3;

const LEVEL_COLORS: Record<string, string> = {
    franchise: "#6f42c1",
    shop: "#0d6efd",
    application: "#198754",
};

export function PosConfigurationModal() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const mainColor = useSelector(
        (state: RootState) => state.global.infoUser.maincolor
    );
    const {
        isModalOpened,
        isLoading,
        isSaving,
        isConsultation,
        showPropagationConfirm,
        configLevel,
        selectedRow,
        config,
        errorMessage,
    } = useSelector((state: RootState) => state.posConfig);

    React.useEffect(() => {
        if (errorMessage) {
            ErrorToast(errorMessage);
            dispatch(clearErrorMessage());
        }
    }, [errorMessage, dispatch]);
    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

    const hasInfoModes = React.useMemo(() => {
        const activeSaleModes = config.SalesMode.items.filter(
            (sm: POSSalesModeItem) => sm.active
        );
        return activeSaleModes.some((sm: POSSalesModeItem) =>
            config.InformationMode.items.some(
                (item: POSInformationModeItem) => {
                    const keys = Object.keys(item.active);
                    return keys.some(
                        (k) =>
                            k === sm.name ||
                            k === sm.id ||
                            k === sm.displayName?.FR ||
                            k === sm.displayName?.EN
                    );
                }
            )
        );
    }, [config.SalesMode.items, config.InformationMode.items]);

    const levelLabel: Record<string, string> = {
        franchise: t("Franchise"),
        shop: t("Shop"),
        application: t("Application"),
    };

    const levelDescription: Record<string, string> = {
        franchise: t(
            "Changes will be applied to all shops and applications below"
        ),
        shop: t("Changes will be applied to applications of this shop"),
        application: t("Changes apply only to this application"),
    };

    const toggleModal = useCallback(() => {
        dispatch(setIsModalOpened(false));
    }, [dispatch]);

    const toggleNav = useCallback(
        (tab: number) => {
            if (activeTab !== tab) setActiveTab(tab);
        },
        [activeTab]
    );

    const handleSaveResult = useCallback(
        (result: { success: boolean; errors: string[] }) => {
            if (!result.success) {
                result.errors.forEach((err) => ErrorToast(err));
                return false;
            }
            showSuccessToast(t("POS configuration saved successfully"));
            return true;
        },
        [t]
    );

    const handleSave = useCallback(async () => {
        try {
            const result = await dispatch(
                savePosConfig({ propagateToChildren: false, t })
            ).unwrap();
            if (result.needsConfirmation) {
                dispatch(setIsModalOpened(false));
                return;
            }
            if (handleSaveResult(result)) toggleModal();
        } catch {
            ErrorToast(t("An error occurred while saving the configuration"));
        }
    }, [dispatch, handleSaveResult, toggleModal, t]);

    const handleConfirmPropagate = useCallback(async () => {
        dispatch(setShowPropagationConfirm(false));
        try {
            const result = await dispatch(
                savePosConfig({ propagateToChildren: true, t })
            ).unwrap();
            handleSaveResult(result);
        } catch {
            ErrorToast(t("An error occurred while saving the configuration"));
        }
    }, [dispatch, handleSaveResult, t]);

    const handleSkipPropagate = useCallback(async () => {
        dispatch(setShowPropagationConfirm(false));
        try {
            const result = await dispatch(persistCurrentLevelOnly()).unwrap();
            handleSaveResult(result);
        } catch {
            ErrorToast(t("An error occurred while saving the configuration"));
        }
    }, [dispatch, handleSaveResult, t]);

    const levelColor = LEVEL_COLORS[configLevel];

    const cssVars = React.useMemo(
        () =>
            ({
                "--pos-level-color": levelColor,
                "--pos-main-color": mainColor,
            } as React.CSSProperties),
        [levelColor, mainColor]
    );

    return (
        <>
            <Modal
                centered
                backdrop="static"
                fade={false}
                scrollable
                toggle={toggleModal}
                isOpen={isModalOpened}
                className="modal-wrapper__clz"
                style={cssVars}
                data-testid="pos-config-modal"
            >
                <ModalHeader
                    toggle={toggleModal}
                    className="text-uppercase modal-wrapper-title__clz"
                    data-testid="pos-config-modal-header"
                >
                    {isConsultation
                        ? t("POS Configuration (consultation)")
                        : t("POS Configuration")}{" "}
                    - {levelLabel[configLevel]}
                    {selectedRow?.[t("Designation")]
                        ? ` : ${selectedRow[t("Designation")]}`
                        : ""}
                </ModalHeader>
                <ModalBody className="p-0">
                    {/* Indicateur de niveau */}
                    <div
                        className="d-flex align-items-center px-4 py-1 pos-level-indicator__clz"
                        style={{ borderLeft: `4px solid ${mainColor}` }}
                    >
                        <span
                            className="pos-level-badge__clz"
                            style={{ backgroundColor: mainColor }}
                        >
                            {levelLabel[configLevel]}
                        </span>
                        <span className="pos-level-description__clz">
                            {levelDescription[configLevel]}
                        </span>
                    </div>

                    {isLoading && (
                        <div className="d-flex justify-content-center align-items-center pos-loading-container__clz">
                            <div
                                className="spinner-border pos-loading-spinner__clz"
                                role="status"
                            >
                                <span className="sr-only">
                                    {t("Loading...")}
                                </span>
                            </div>
                        </div>
                    )}
                    <div
                        className={classNames("pos-config-grid__clz", {
                            "pos-config-grid-collapsed__clz": isSidebarCollapsed,
                            "d-none": isLoading,
                        })}
                    >
                        <Nav tabs className="pos-config-sidebar__clz">
                            {/* Toggle collapse button */}
                            <div
                                className="pos-sidebar-toggle__clz"
                                data-testid="pos-config-sidebar-toggle"
                                role="button"
                                tabIndex={0}
                                aria-label={t("Toggle sidebar")}
                                aria-expanded={
                                    isSidebarCollapsed ? "false" : "true"
                                }
                                onClick={() =>
                                    setIsSidebarCollapsed(!isSidebarCollapsed)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setIsSidebarCollapsed(
                                            !isSidebarCollapsed
                                        );
                                    }
                                }}
                            >
                                <MenuBurgerIcon
                                    fill="white"
                                    height={20}
                                    width={20}
                                />
                            </div>
                            {TAB_CONFIG.map((tab, index) => {
                                const IconComp = tab.icon;
                                const isDisabled =
                                    index === INFORMATION_MODE_TAB_INDEX &&
                                    !hasInfoModes;
                                return (
                                    <NavItem
                                        key={tab.titleKey}
                                        className="pb-1"
                                        data-testid={`pos-config-tab-${index}`}
                                    >
                                        <NavLink
                                            className={classNames(
                                                "border-0 pos-sidebar-navlink__clz",
                                                {
                                                    "active pos-sidebar-navlink-active__clz":
                                                        activeTab === index,
                                                    "pos-sidebar-navlink-disabled__clz": isDisabled,
                                                }
                                            )}
                                            onClick={() => {
                                                if (!isDisabled)
                                                    toggleNav(index);
                                            }}
                                            title={
                                                isSidebarCollapsed
                                                    ? t(tab.titleKey)
                                                    : undefined
                                            }
                                            disabled={isDisabled}
                                        >
                                            <IconComp size={20} />
                                            {!isSidebarCollapsed && (
                                                <span className="pos-sidebar-label__clz">
                                                    {t(tab.titleKey)}
                                                </span>
                                            )}
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>

                        <TabContent
                            activeTab={activeTab}
                            className="pos-config-content__clz"
                            style={
                                isConsultation
                                    ? { pointerEvents: "none", opacity: 0.85 }
                                    : undefined
                            }
                        >
                            {TAB_CONFIG.map((tab, index) => (
                                <TabPane
                                    tabId={index}
                                    key={tab.titleKey}
                                    className="pos-tab-pane__clz"
                                >
                                    {activeTab === index &&
                                        React.createElement(tab.component)}
                                </TabPane>
                            ))}
                        </TabContent>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {isConsultation ? (
                        <CustomMainColorButton
                            className="w-20"
                            rounded
                            color={mainColor}
                            onClick={toggleModal}
                            data-testid="pos-config-close-btn"
                        >
                            {t("Close")}
                        </CustomMainColorButton>
                    ) : (
                        <CustomMainColorButton
                            className="w-20"
                            rounded
                            color={mainColor}
                            onClick={handleSave}
                            disabled={isSaving}
                            data-testid="pos-config-save-btn"
                        >
                            {isSaving ? t("Saving...") : t("Save")}
                        </CustomMainColorButton>
                    )}
                </ModalFooter>
            </Modal>

            {/* Modal de confirmation de propagation */}
            <Modal
                isOpen={showPropagationConfirm}
                data-testid="pos-propagation-modal"
                className="pos-propagation-modal__clz"
            >
                <ModalHeader
                    toggle={() => {
                        dispatch(setShowPropagationConfirm(false));
                    }}
                >
                    <span className="text-capitalize">{t("Alert")}</span>
                </ModalHeader>
                <ModalBody>
                    <span className="pl-3 m-0 pos-propagation-message__clz">
                        {configLevel === "franchise"
                            ? t(
                                  "Attention, if you click on validate, the specific configuration of the shops will be edited."
                              )
                            : t(
                                  "Be careful, if you click on validate, the specific configuration of the applications will be edited."
                              )}
                        !
                    </span>
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <CustomMainColorButton
                        outline
                        rounded
                        color={mainColor}
                        onClick={handleSkipPropagate}
                        disabled={isSaving}
                        data-testid="pos-propagation-skip"
                    >
                        {t("Cancel")}
                    </CustomMainColorButton>
                    <CustomMainColorButton
                        rounded
                        className="mr-2"
                        color={mainColor}
                        onClick={handleConfirmPropagate}
                        disabled={isSaving}
                        data-testid="pos-propagation-confirm"
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </ModalFooter>
            </Modal>
        </>
    );
}
